import { z } from 'zod';
import { AffineMatrix, Checksum, Millimeters, PointMm, Uuid } from './primitives.js';
import { Color } from './color.js';

/** Modos de composicion admitidos. Ampliar exige version de esquema + migrador. */
export const BlendMode = z.enum(['normal', 'multiply', 'screen']);
export type BlendMode = z.infer<typeof BlendMode>;

export const LayerBase = z.object({
  id: Uuid,
  /** Etiqueta legible para el panel de capas. Anadido en schemaVersion 2. */
  name: z.string().max(120).optional(),
  z: z.number().int(),
  transform: AffineMatrix,
  anchor: z.enum(['top-left', 'center', 'custom']),
  anchorPoint: PointMm.optional(),
  opacity: z.number().min(0).max(1).default(1),
  blend: BlendMode.default('normal'),
  clip: z.object({ polygonMm: z.array(PointMm).min(3) }).optional(),
  locked: z.boolean().default(false),
});

/**
 * El shaping tipografico NO es reproducible entre versiones de motor (R-4).
 * Por eso al congelar se guarda el layout resuelto completo Y el contorno
 * aprobado como derivado firmado: lo aprobado es un fichero, no la promesa de
 * que el shaping vuelva a salir igual.
 */
export const FrozenTextLayout = z.object({
  engine: z.string().min(1),
  engineVersion: z.string().min(1),
  glyphs: z.array(z.object({ id: z.number().int(), xMm: Millimeters, yMm: Millimeters })),
  variationAxes: z.record(z.number()).optional(),
  substitutions: z.array(z.string()).optional(),
  fallbackFontHash: z.string().optional(),
  /** Asset del contorno aprobado (SVG path), referenciado por checksum. */
  approvedOutlineAssetId: z.string().optional(),
});
export type FrozenTextLayout = z.infer<typeof FrozenTextLayout>;

export const TextLayer = LayerBase.extend({
  kind: z.literal('text'),
  /** SIEMPRE legible y editable: sin esto no se puede reeditar ni auditar el pedido. */
  text: z.string(),
  /**
   * PAPEL de este texto en una SERIE de equipo (D-4/D-5 del plan de mejoras).
   *
   * Un club encarga dieciocho camisetas iguales salvo nombre y dorsal: el
   * diseño BASE marca qué capa es el nombre y cuál el dorsal, los miembros
   * viven en su tabla, y la sustitución ocurre al previsualizar, validar y
   * producir. El texto de la capa sigue siendo el del ejemplo.
   *
   * OPCIONAL y en la v3 a propósito: no invalida ningún documento existente ni
   * exige migración — los migradores encadenados están para cambios que ROMPEN,
   * y este no rompe. El coste real es regenerar vectores dorados y oráculo de
   * paridad, que es exactamente lo que hay que hacer al tocar este fichero.
   */
  rol: z.enum(['nombre', 'dorsal']).optional(),
  font: z.object({
    family: z.string().min(1),
    version: z.string().min(1),
    /** Hash de los BYTES EXACTOS del fichero de fuente. */
    fileHash: z.string().min(1),
    /**
     * Peso y estilo REALES, no simulados.
     *
     * Cada combinacion es un fichero distinto con su propio `fileHash`: engordar
     * una letra por software da un borde sucio que en serigrafia se ve, y el
     * compositor de produccion necesita el fichero exacto.
     */
    weight: z.number().int().min(100).max(900).default(400),
    italic: z.boolean().default(false),
  }),
  sizeMm: Millimeters.refine((v) => v > 0, 'sizeMm debe ser > 0'),
  tracking: z.number().finite(),
  /** Interlineado en MILIMETROS (schemaVersion 2; en v1 era un multiplicador). */
  lineHeightMm: Millimeters.refine((v) => v > 0, 'lineHeightMm debe ser > 0'),
  direction: z.enum(['ltr', 'rtl']).default('ltr'),
  /** Alineacion dentro de la caja de texto. */
  align: z.enum(['left', 'center', 'right', 'justify']).default('left'),
  /** Transformacion visual; el texto ORIGINAL se conserva intacto en `text`. */
  textCase: z.enum(['none', 'upper', 'lower']).default('none'),
  color: Color,
  /**
   * Contorno. Un texto claro sobre prenda clara no se lee sin el, y el grosor
   * va en milimetros para que la regla de la tecnica pueda juzgarlo.
   */
  outline: z
    .object({ color: Color, widthMm: Millimeters.refine((v) => v > 0, 'el contorno debe medir > 0') })
    .optional(),
  /**
   * Texto en arco: el radio en milimetros y hacia donde abre.
   *
   * Es la forma de un escudo de club o de una pena, y sin el la mitad de los
   * encargos tipicos no se pueden hacer aqui.
   */
  arc: z
    .object({
      radiusMm: Millimeters.refine((v) => v > 0, 'el radio debe ser > 0'),
      direction: z.enum(['up', 'down']),
    })
    .optional(),
  frozenLayout: FrozenTextLayout.optional(),
});
export type TextLayer = z.infer<typeof TextLayer>;

/**
 * Recorte de la imagen ORIGEN, en fraccion del fichero (0 a 1).
 *
 * Va en fracciones y no en pixeles a proposito: asi el recorte sobrevive a que
 * el mismo asset se sirva en una version de menos resolucion para el editor.
 * Y es un recorte de la FUENTE, distinto del `clip` de `LayerBase`, que recorta
 * la capa ya colocada sobre el diseno.
 */
export const RecorteImagen = z
  .object({
    xFrac: z.number().min(0).max(1),
    yFrac: z.number().min(0).max(1),
    widthFrac: z.number().gt(0).max(1),
    heightFrac: z.number().gt(0).max(1),
  })
  .refine((r) => r.xFrac + r.widthFrac <= 1.0001 && r.yFrac + r.heightFrac <= 1.0001, {
    message: 'el recorte se sale de la imagen',
  });
export type RecorteImagen = z.infer<typeof RecorteImagen>;

/**
 * Ajustes de imagen.
 *
 * Pasar una foto a UNA TINTA es lo que la hace imprimible en serigrafia barata,
 * y el alto contraste resuelve la mayoria de los logotipos escaneados. Los
 * numeros son multiplicadores respecto al original —1 es «como esta»— para que
 * un valor por defecto no cambie nada.
 */
export const AjustesImagen = z.object({
  brillo: z.number().min(0).max(3).default(1),
  contraste: z.number().min(0).max(3).default(1),
  saturacion: z.number().min(0).max(3).default(1),
  /** 'ninguno' | 'grises' | 'contraste-alto' | 'una-tinta'. */
  modo: z.enum(['ninguno', 'grises', 'contraste-alto', 'una-tinta']).default('ninguno'),
  /** Umbral del alto contraste y de la tinta unica, de 0 a 1. */
  umbral: z.number().min(0).max(1).default(0.5),
  /** Color de la tinta en modo 'una-tinta'. */
  tinta: Color.optional(),
});
export type AjustesImagen = z.infer<typeof AjustesImagen>;

export const ImageLayer = LayerBase.extend({
  kind: z.literal('image'),
  /** NUNCA una ruta. */
  assetChecksum: Checksum,
  intrinsic: z.object({
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
    dpi: z.number().positive().optional(),
  }),
  /*
    Los dos son OPCIONALES y sin valor por defecto: asi un documento que no los
    lleva sigue teniendo exactamente el mismo hash que antes de que existieran.
    Un `.default()` aqui habria obligado a subir la version del esquema.
  */
  recorte: RecorteImagen.optional(),
  ajustes: AjustesImagen.optional(),
});
export type ImageLayer = z.infer<typeof ImageLayer>;

export const ShapeLayer = LayerBase.extend({
  kind: z.literal('shape'),
  /** Path SVG cuyas coordenadas estan en MILIMETROS. */
  path: z.string().min(1),
  fill: Color.optional(),
  stroke: Color.optional(),
  strokeWidthMm: Millimeters.optional(),
});
export type ShapeLayer = z.infer<typeof ShapeLayer>;

export const Layer = z.discriminatedUnion('kind', [TextLayer, ImageLayer, ShapeLayer]);
export type Layer = z.infer<typeof Layer>;
